import { showError } from "nsitools-react";
import * as React from "react";
import { useHistory, useParams } from "react-router";

import { SuiviHopeForm } from "../../../..";
import { EFormePersonne, SiegeSocialSuiviApi, SuiviApi, SuiviHopeEditDto } from "../../../../../../api";
import { ERoutes } from "../../../../../../AppRouter";
import { useAuth, useDialog } from "../../../../../../contexts";
import { useApiService, useCrudApi, useTabMessage, useTl } from "../../../../../../hooks";
import { ETLCodes } from "../../../../../../locales";
import { exportFile } from "../../../../../../utils";
import { Guid } from "../../../../../../utils/guid";

export interface ISiegeLieuSuiviProps {
  idsuivi: number;
  idsiegeSocial: number;
  idlieuFormation?: number;
  editMode: boolean;
  backRoute: string;
}

export const SiegeLieuSuivi: React.FunctionComponent<ISiegeLieuSuiviProps> = ({
  editMode,
  idsiegeSocial,
  idsuivi,
  idlieuFormation
}) => {
  const { t } = useTl();
  const api = useApiService(SiegeSocialSuiviApi);
  const apiF = useApiService(SuiviApi);
  const { user } = useAuth();
  const history = useHistory();
  const uniqueIdentifier = React.useMemo(() => Guid.newGuid(), []);
  const { sendMessage } = useTabMessage(uniqueIdentifier);
  const { id, state, forme } = useParams<{
    id: string;
    tab: string;
    state: string;
    forme: EFormePersonne;
  }>();

  const { data, loading, saveItem, saving, deleteItem, deleting, validationErrors, refresh } = useCrudApi<
    SuiviHopeEditDto
  >(
    React.useMemo(
      () => ({
        getApiFn: async () => {
          const suivi = await api.siegeSocialSuiviGetSuiviHope({
            id: +idsuivi
          });
          if (+idsuivi <= 0) {
            suivi.idsuivi = 0;
            suivi.idrealisateurSuivi = user.iduser;
            suivi.creationUser = user.iduser;
            suivi.encodeur = user.nom + " " + user.prenom;
            suivi.dateSuivi = new Date();
            suivi.idsiegeSocial = idsiegeSocial;
            suivi.idlieuFormation = idlieuFormation;
          }
          return suivi;
        },
        closeCfs: false,
        saveApiFn: d => api.siegeSocialSuiviSaveSuiviHope({ SuiviHopeEditDto: d }),
        onSavedRoute: d =>
          !!idlieuFormation
            ? `${ERoutes.lieuFormation}/${idlieuFormation}/suivi/edit/${d.idsuivi}?idsiegeSocial=${d.idsiegeSocial}`
            : `${ERoutes.siegeSocial}/${d.idsiegeSocial}/suivi/edit/${d.idsuivi}`,
        onDeletedRoute: d =>
          !!idlieuFormation
            ? `${ERoutes.lieuFormation}/${idlieuFormation}/suivi/edit?idsiegeSocial=${d.idsiegeSocial}`
            : `${ERoutes.siegeSocial}/${d.idsiegeSocial}/suivi`,
        onSaved: d => sendMessage(d.idsuivi, true),
        getDeps: [idsuivi],
        deleteApiFn: d => api.siegeSocialSuiviDeleteSiegeLieuSuivi({ id: d.idsuivi })
      }),
      [idsuivi, api, user.iduser, user.nom, user.prenom, idsiegeSocial, idlieuFormation, sendMessage]
    )
  );

  const onCancel = React.useCallback(() => history.push(`${ERoutes.contacts}/${id}/suivis/${state}`), [
    history,
    id,
    state
  ]);

  const [rowLoading, setRowLoading] = React.useState(null);
  const { showDialogPromise } = useDialog();
  const deleteFichier = React.useCallback(
    async (idsuiviFichier: number) => {
      const result = await showDialogPromise({
        message: t(ETLCodes.DeleteConfirmationMessage)
      });

      if (result === "yes") {
        try {
          setRowLoading(idsuiviFichier);
          await apiF.suiviDeleteSuiviFichier({
            id: idsuiviFichier
          });
          refresh();
        } catch {
          showError(t(ETLCodes.ErrorWhileDeletingFile));
        } finally {
          setRowLoading(null);
        }
      }
    },
    [apiF, refresh, showDialogPromise, t]
  );

  const downloadFichier = React.useCallback(
    async (idsuiviFichier: number) => {
      try {
        setRowLoading(idsuiviFichier);
        const file = await apiF.suiviDownloadSuiviFichier({
          id: idsuiviFichier
        });
        await exportFile(file);
      } catch {
        showError(t(ETLCodes.ErrorWhileDownloadingFile));
      } finally {
        setRowLoading(null);
      }
    },
    [apiF, t]
  );

  return (
    <SuiviHopeForm
      data={data}
      suiviId={+idsuivi}
      saveItem={saveItem}
      loading={loading}
      saving={saving}
      editMode={editMode && !data?.fromSmile}
      deleteItem={deleteItem}
      deleting={deleting}
      onCancel={onCancel}
      validationErrors={validationErrors}
      fieldsetTitle={t(ETLCodes.Suivi)}
      deleteFichier={deleteFichier}
      downloadFichier={downloadFichier}
      rowLoading={rowLoading}
      refreshData={refresh}
      forme={forme}
    />
  );
};
